/**
 * @name WorkAnniversaries
 * @description Componente de próximos aniversarios laborales
 * @author César Rivera
 */

import React from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import moment from 'moment';
import styled from 'styled-components';

import { EmployeeDetail } from '@components';
import { Card, MUI, Unicons, Typography } from '@styles';

const WorkAnniversaries = (props) => {
  const { anniversaries, onOpen } = props;

  const month = new Date().getMonth();
  const today = new Date();
  const tomorrow = moment().add(1, 'd').format('DD/MM');

  const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
    'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
  ];

  // Próximos aniversarios laborales
  const anniversariesFilter = (a, b) => {
    let x = String(moment(a).format('DD/MM/YYYY'));
    let y = String(moment(b).format('DD/MM/YYYY'));
    return parseInt(x.substring(0, 2)) >= parseInt(y.substring(0, 2)) || parseInt(x.substring(3, 5)) > parseInt(y.substring(3, 5));
  };

  const nextAnniversaries = anniversaries.filter((item) => anniversariesFilter(item.FechaAniversario, today));

  return (
    <>
      {/* HEADER */}
      <Card.Header divider>
        <Card.HeaderTitle>
          <Typography.H5>{i18n.t('Home.anniversaries')}</Typography.H5>
          <Typography.Text color="greyDarkAlpha">
            {`${i18n.t('GenericMessages.month')}: `}
            <Typography.Span>{i18n.t(`Months.${[monthNames[month]]}`)}</Typography.Span>
          </Typography.Text>
        </Card.HeaderTitle>
        {anniversaries.length !== 0 && (
          <Card.HeaderAction>
            <MUI.Tooltip title={i18n.t('GenericMessages.seeAll')}>
              <MUI.ButtonIcon onClick={onOpen}>
                <Unicons.EllipsisH size="20" />
              </MUI.ButtonIcon>
            </MUI.Tooltip>
          </Card.HeaderAction>
        )}
      </Card.Header>
      {/* CONTENT */}
      <Card.Content scroll>
        {nextAnniversaries.length !== 0 ? (
          nextAnniversaries.map((item, index) => {
            let workDate = moment(item.FechaAniversario).format('DD/MM');
            if (workDate === moment(today).format('DD/MM')) {
              workDate = i18n.t('Home.today');
            } else if (workDate === tomorrow) {
              workDate = i18n.t('Home.tomorrow');
            } else {
              workDate = moment(item.FechaAniversario).format(i18n.t('FormatDates.birthday'));
            }

            let seniority = `${item.Antiguedad} ${i18n.t('GenericMessages.years').toLowerCase()}`;

            return (
              <BoxRow key={index}>
                <WorkAnnEmployee>
                  <EmployeeDetail
                    avatar={item.Foto}
                    avatarSize={45}
                    employeeName={<Typography.Medium>{item.Nombres}</Typography.Medium>}
                    employeeDetail={<Typography.Text>{item.Area}</Typography.Text>}
                    employeeSubDetail={<Typography.Text color="greyDarkAlpha">{item.Sucursal}</Typography.Text>}
                  />
                </WorkAnnEmployee>
                <WorkAnnDay>
                  <Typography.Text>{workDate}</Typography.Text>
                  <Typography.Span>{seniority}</Typography.Span>
                </WorkAnnDay>
              </BoxRow>
            );
          })
        ) : (
          <BoxNotFound>
            <Unicons.CircleExclamation size="20" style={{ marginRight: '12px' }} />
            <Typography.H6>{`${i18n.t('Home.noAnniversariesFound')} ${i18n.t(`Months.${[monthNames[month]]}`)}`}</Typography.H6>
          </BoxNotFound>
        )}
      </Card.Content>
    </>
  );
};

WorkAnniversaries.propTypes = {
  // Array con la información de los aniversarios laborales del mes.
  anniversaries: PropTypes.array.isRequired,
  // Función que se ejecuta para abrir el modal.
  onOpen: PropTypes.func.isRequired
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const BoxRow = styled.div`
  display: flex;
  padding: 8px;
`;

const BoxNotFound = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;

  .MuiSvgIcon-root {
    margin-right: 12px;
    font-size: 20px;
  }
`;

const WorkAnnEmployee = styled.div`
  width: 75%;
`;

const WorkAnnDay = styled.div`
  display: flex;
  flex-direction: column;
  width: 25%;
  align-self: center;
  text-align: end;
  padding: 8px;

  & > p {
    color: ${(props) => props.theme.colors.blueMedium};
  }
`;

export default WorkAnniversaries;